import DistanceMeasurement2D from '@arcgis/core/widgets/DistanceMeasurement2D.js';
import AreaMeasurement2D from '@arcgis/core/widgets/AreaMeasurement2D.js';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import { getMapView } from '@/map/mapStore';
import { commitMeasurement } from '@/map/measurements';
import { registerHandle, summarizeRef, type ResolvedRef } from '@/agent/refs';
import { onAgentAbort } from '@/agent/abort';
import type { Tool } from './types';

type Mode = 'distance' | 'area';
type Input = { mode: Mode };

type MeasureResult = { value: number; units: string; handle: string };

export const measure: Tool<Input, MeasureResult> = {
  name: 'measure',
  description:
    'Activate measurement: distance (polyline) or area (polygon). User draws on the map with a live readout; double-click to commit. The committed measurement stays on the map until cleared. Returns the value in meters / square meters and a handle to the geometry.',
  input_schema: {
    type: 'object',
    properties: { mode: { enum: ['distance', 'area'] } },
    required: ['mode'],
    additionalProperties: false,
  },
  execute: ({ mode }) =>
    new Promise<MeasureResult>((resolve, reject) => {
      const view = getMapView();
      if (!view) {
        reject(new Error('measure: map not initialised'));
        return;
      }

      const widget =
        mode === 'distance'
          ? new DistanceMeasurement2D({ view, unit: 'metric' })
          : new AreaMeasurement2D({ view, unit: 'metric' });
      view.ui.add(widget, 'top-right');

      let settled = false;
      const cleanup = () => {
        watchHandle.remove();
        document.removeEventListener('keydown', onKeyDown);
        offAbort();
        view.ui.remove(widget);
        widget.destroy();
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Escape' || settled) return;
        settled = true;
        cleanup();
        reject(new Error('measure: user cancelled'));
      };
      document.addEventListener('keydown', onKeyDown);

      const offAbort = onAgentAbort(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('measure: aborted'));
      });

      const onMeasured = () => {
        if (settled) return;
        const m = widget.viewModel.measurement as
          | { length?: number; area?: number; perimeter?: number; geometry?: Geometry }
          | null
          | undefined;
        const geometry = m?.geometry as Geometry | undefined;
        if (!geometry) {
          settled = true;
          cleanup();
          reject(new Error('measure: no geometry produced'));
          return;
        }

        const valueMeters =
          mode === 'distance' ? Number(m?.length ?? 0) : Number(m?.area ?? 0);
        const perimeterMeters =
          mode === 'area' ? Number(m?.perimeter ?? 0) : undefined;

        commitMeasurement({ mode, geometry, valueMeters, perimeterMeters });

        const ref: ResolvedRef = {
          source_kind: 'geometry',
          geometry,
          extent: (geometry as { extent?: __esri.Extent }).extent ?? undefined,
        };
        const id = registerHandle(ref);
        void summarizeRef(ref);

        settled = true;
        cleanup();
        resolve({
          value: valueMeters,
          units: mode === 'distance' ? 'meters' : 'square-meters',
          handle: id,
        });
      };

      const watchHandle = reactiveUtils.watch(
        () => widget.viewModel.state,
        (state) => {
          if (state === 'measured') onMeasured();
        },
      );

      // Kick off drawing on the next tick so the widget is fully initialised.
      queueMicrotask(() => {
        try {
          widget.viewModel.start();
        } catch {
          // some builds auto-start; ignore
        }
      });
    }),
};
